'use client';
/** @jsxImportSource @emotion/react */
import { forwardRef } from 'react';

import { alpha, type Theme, type SxProps } from '@mui/material';

import { Badge } from '../badge/Badge';
import { Box } from '../box/Box';
import { ButtonBase } from '../button-base/ButtonBase';
import { CoreIcon } from '../core-icon/CoreIcon';
interface SelectionChipProps
  extends Omit<
    React.ComponentProps<'button'>,
    'onClick' | 'disabled' | 'aria-pressed'
  > {
  /**
   * The label of the chip.
   */
  label: React.ReactNode;
  /**
   * The variant to use.
   *
   * - `"toggle"` will render a chip representing a togglable option
   * - `"action"` will render a chip that can act as a trigger for a dropdown or popover element
   * - `"dismissable"` will render a chip that can be removed
   *
   * @default "toggle"
   */
  variant?: 'toggle' | 'action' | 'dismissable';
  /**
   * If `true`, the chip is selected. If used with the `action` variant, use this to indicate that a selection has been made in a dropdown or popover triggered by the chip.
   *
   * @default false
   */
  selected?: boolean;
  /**
   * If `true`, the chip will have an elevated style.
   *
   * @default false
   */
  elevated?: boolean;
  /**
   * Content for the badge to display on the chip. Only applies when used in combination with the `action` variant to indicate the number of selected items in a dropdown or popover triggered by the chip.
   */
  badgeContent?: {
    value: number;
    ariaLabel: string;
  };
  /**
   * The color of the component.
   *
   * @default "primary"
   */
  color?: 'primary' | 'forceWhite';
  /**
   * If `true`, the chip will be disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The icon to display at the start of the chip. Expects a Zeus `<Icon>` component.
   */
  startIcon?: React.ReactElement;
  /**
   * If `true`, the chip will take up the full width of its container.
   */
  fullWidth?: boolean;
  /**
   * Callback fired when the chip is clicked.
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
}

/**
 * Static padding — same value for all states since border is via box-shadow
 * and doesn't affect the box model.
 */
const getSpacingStyles = (): SxProps<Theme> => (theme: Theme) => ({
  padding: theme.spacing(0, 1.5), // 0 12px
});

/**
 * Combined border + elevation shadow styles.
 *
 * Borders use `box-shadow: inset` instead of CSS `border` because border
 * width changes between states (0/1/2px) cause sub-pixel layout shift on
 * Windows at fractional zoom levels — the browser rounds border and padding
 * independently. Box-shadow doesn't affect the box model, keeping dimensions
 * stable across states and zoom levels.
 */
const getShadowStyles =
  (
    selected: boolean,
    elevated: boolean,
    disabled: boolean,
    color: 'primary' | 'forceWhite'
  ): SxProps<Theme> =>
  (theme: Theme) => {
    const isForceWhiteFlat = color === 'forceWhite' && !elevated;
    const isForceWhiteElevated = color === 'forceWhite' && elevated;

    let borderColor = theme.palette.secondary.light;
    if (disabled) {
      borderColor = theme.palette.action.disabled;
    } else if (isForceWhiteFlat) {
      borderColor = theme.palette.forceWhite.main;
    } else if (isForceWhiteElevated) {
      borderColor = theme.palette.common.black;
    } else if (selected) {
      borderColor = theme.palette.primary.main;
    }

    const borderWidth = selected ? '2px' : '1px';
    const insetShadow = `inset 0 0 0 ${borderWidth} ${borderColor}`;
    const elevationShadow = theme.shadows[3];

    let boxShadow: string;
    if (elevated && selected) {
      boxShadow = `${insetShadow}, ${elevationShadow}`;
    } else if (elevated) {
      boxShadow = elevationShadow;
    } else {
      boxShadow = insetShadow;
    }

    return {
      border: 'none',
      borderRadius: `${theme.shape.borderRadiusFull}px`,
      boxShadow,
    };
  };

/**
 * Get color styles (text, background) based on state
 */
const getColorStyles =
  (
    selected: boolean,
    disabled: boolean,
    color: 'primary' | 'forceWhite',
    elevated: boolean
  ): SxProps<Theme> =>
  (theme: Theme) => {
    const isForceWhiteFlat = color === 'forceWhite' && !elevated;
    const isForceWhiteElevated = color === 'forceWhite' && elevated;
    const isPrimary = color === 'primary';
    let textColor = theme.palette.text.primary;
    if (disabled) {
      textColor = theme.palette.action.disabled;
    } else if (isForceWhiteFlat) {
      textColor = theme.palette.forceWhite.main;
    } else if (isForceWhiteElevated) {
      textColor = theme.palette.common.black;
    } else if (selected) {
      textColor = theme.palette.primary.main;
    }

    // Only elevated chips have non-transparent backgrounds
    let backgroundColor = 'transparent';
    if (disabled && (isForceWhiteElevated || isPrimary)) {
      backgroundColor = theme.palette.action.disabledBackground;
    } else if (elevated) {
      backgroundColor = theme.palette.common.white;
    }

    return {
      color: textColor,
      background: backgroundColor,
    };
  };

/**
 * Get interaction styles (cursor)
 */
const getInteractionStyles =
  (disabled: boolean): SxProps<Theme> =>
  () => ({
    '@media (hover: hover)': {
      '&:hover': {
        cursor: disabled ? 'not-allowed' : 'pointer',
      },
    },
  });
/**
 * Get overlay styles for the hover/selected state element
 */
const getOverlayStyles =
  (
    selected: boolean,
    disabled: boolean,
    color: 'primary' | 'forceWhite',
    elevated: boolean
  ): SxProps<Theme> =>
  (theme: Theme) => {
    const isForceWhiteFlat = color === 'forceWhite' && !elevated;
    const isForceWhiteElevated = color === 'forceWhite' && elevated;
    const getOverlayColor = (isSelected: boolean): string => {
      if (isForceWhiteFlat) {
        const opacity = isSelected
          ? theme.palette.action.selectedOpacity
          : theme.palette.action.hoverOpacity;
        return alpha(theme.palette.forceWhite.main, opacity);
      }
      if (isForceWhiteElevated && isSelected) {
        return alpha(
          theme.palette.common.black,
          theme.palette.action.selectedOpacity
        );
      }
      if (isSelected) {
        return alpha(
          theme.palette.primary.main,
          // We deliberately use hoverOpacity for the selected state due to a11y contrast
          // issues with primary.main at selectedOpacity for Booking.com
          theme.palette.action.hoverOpacity
        );
      }
      return theme.palette.action.hover;
    };
    return {
      zIndex: -1,
      position: 'absolute',
      inset: 0,
      borderRadius: `${theme.shape.borderRadiusFull}px`,
      backgroundColor: getOverlayColor(selected && !disabled),
      opacity: selected && !disabled ? 1 : 0,
      pointerEvents: 'none',
      '.MuiButtonBase-root.Mui-focusVisible &':
        !disabled && !selected ? { opacity: 1 } : {},
      '@media (hover: hover)': {
        '.MuiButtonBase-root:hover &':
          !disabled && !selected ? { opacity: 1 } : {},
      },
    };
  };
/**
 * Get focus styles
 */
const getFocusStyles =
  (color: SelectionChipProps['color']): SxProps<Theme> =>
  (theme: Theme) => ({
    '&:focus, &:focus-visible, &.Mui-focusVisible': {
      outline: 'none',
    },
    '&.Mui-focusVisible::after': {
      content: '""',
      borderRadius: theme.ui.focus.borderRadius,
      inset: '0px',
      position: 'absolute',
      outline: `${theme.ui.focus.outlineWidth} solid ${color === 'forceWhite' ? theme.palette.common.white : theme.palette.action.focusOutline}`,
      outlineOffset: theme.ui.focus.outlineOffset,
      zIndex: 1,
    },
  });
/**
 * Get badge styles for action variant
 */
const getBadgeStyles =
  (disabled: boolean, color: 'primary' | 'forceWhite', elevated: boolean) =>
  (theme: Theme) => {
    const isForceWhiteFlat = color === 'forceWhite' && !elevated;
    const isForceWhiteElevated = color === 'forceWhite' && elevated;
    let badgeStyles: {
      color?: string;
      backgroundColor?: string;
    } = {};
    if (disabled) {
      badgeStyles = { backgroundColor: theme.palette.action.disabled };
    } else if (isForceWhiteFlat) {
      badgeStyles = {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.forceWhite.main,
      };
    } else if (isForceWhiteElevated) {
      badgeStyles = {
        color: theme.palette.forceWhite.main,
        backgroundColor: theme.palette.common.black,
      };
    }
    return {
      display: 'block',
      position: 'relative',
      '& .MuiBadge-badge': {
        transform: 'none',
        position: 'static',
        ...badgeStyles,
      },
    } as const;
  };
const SelectionChip = forwardRef(
  (
    {
      variant = 'toggle',
      label,
      disabled = false,
      startIcon,
      sx = [],
      color = 'primary',
      elevated = false,
      onClick,
      selected: rawSelected,
      badgeContent,
      fullWidth,
      ...rest
    }: SelectionChipProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const selected = variant === 'dismissable' || rawSelected || false;
    const layoutStyles: SxProps<Theme> = {
      display: 'inline-flex',
      alignItems: 'center',
      height: '36px',
      gap: '6px',
      position: 'relative',
      userSelect: 'none',
      fontWeight: (theme) => theme.ui.selectionChip.label.typography.fontWeight,
      fontSize: (theme) =>
        `${theme.ui.selectionChip.label.typography.fontSize}px`,
      lineHeight: (theme) =>
        `${theme.ui.selectionChip.label.typography.lineHeight}px`,
      letterSpacing: (theme) =>
        `${theme.ui.selectionChip.label.typography.letterSpacing}px`,
      width: fullWidth ? '100%' : undefined,
      maxWidth: '100%',
      zIndex: 0,
    };
    return (
      <ButtonBase
        {...rest}
        sx={[
          layoutStyles,
          getSpacingStyles(),
          getShadowStyles(selected, elevated, disabled, color),
          getColorStyles(selected, disabled, color, elevated),
          getInteractionStyles(disabled),
          getFocusStyles(color),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        ref={ref}
        onClick={onClick}
        aria-pressed={variant === 'toggle' ? selected : undefined}
        disabled={disabled}
      >
        <Box
          sx={getOverlayStyles(selected, disabled, color, elevated)}
          aria-hidden="true"
        />
        {startIcon && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              flexShrink: 0,
              '& > *': { maxWidth: '100%', maxHeight: '100%' },
            }}
          >
            {startIcon}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {label}
        </Box>
        {variant === 'action' &&
          badgeContent?.value &&
          badgeContent?.ariaLabel && (
            <Badge
              color="primary"
              badgeContent={badgeContent.value}
              aria-label={badgeContent.ariaLabel}
              sx={[
                getBadgeStyles(disabled, color, elevated),
                { position: 'relative' },
              ]}
            />
          )}
        {variant === 'action' && (
          <CoreIcon name="chevronupdown" size="xs" sx={{ flexShrink: 0 }} />
        )}
        {variant === 'dismissable' && (
          <CoreIcon name="cross" size="xs" sx={{ flexShrink: 0 }} />
        )}
      </ButtonBase>
    );
  }
);
SelectionChip.displayName = 'SelectionChip';

/**
 * @deprecated Please use `ActionChip`, `ToggleChip`, or `RemovableChip` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-chip-migration--chip-migration-guide Migration guide}
 */
export type { SelectionChipProps };
/**
 * @deprecated Please use `ActionChip`, `ToggleChip`, or `RemovableChip` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-chip-migration--chip-migration-guide Migration guide}
 */
export { SelectionChip };
